import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthFacade, AuthorizationService, fromAuth } from '@iot-platform/auth';
import { FavoriteViewsFacade } from '@iot-platform/data-access/favorite-views';
import { UserPreferencesService } from '@iot-platform/data-access/users';
import { GridEngineFacade, GridScrollStateService } from '@iot-platform/grid-engine';
import { ReleaseNotesComponent } from '@iot-platform/iot-platform-ui';
import { HashUtils } from '@iot-platform/iot-platform-utils';
import { AppName, BusinessProfile } from '@iot-platform/models/common';
import { SidenavMenuBlock } from '@iot-platform/models/i4b';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, noop, Observable } from 'rxjs';
import { AbstractAppShellService } from './abstract-app-shell.service';
import { NavigationTreeService as BaseNavigationTreeService } from './navigation-tree.service';

@Injectable({
  providedIn: 'root'
})
export class AppShellService extends AbstractAppShellService {
  protected readonly navigationTreeService: BaseNavigationTreeService = inject(BaseNavigationTreeService);
  protected readonly store: Store = inject(Store);
  protected privileges$ = this.store.select(fromAuth.selectPrivileges);
  protected readonly authFacade: AuthFacade = inject(AuthFacade);
  protected currentBusinessProfile$ = this.authFacade.currentBusinessProfile$;
  protected readonly authorizationService: AuthorizationService = inject(AuthorizationService);
  protected readonly userPreferencesService: UserPreferencesService = inject(UserPreferencesService);
  protected readonly router: Router = inject(Router);
  protected readonly dialog: MatDialog = inject(MatDialog);
  protected readonly gridScrollStateService: GridScrollStateService = inject(GridScrollStateService);
  protected readonly gridEngineFacade: GridEngineFacade = inject(GridEngineFacade);
  protected readonly favoriteViewsFacade: FavoriteViewsFacade = inject(FavoriteViewsFacade);

  get appName() {
    return AppName.IOT4BOS;
  }

  getCurrentBusinessProfile(): Observable<BusinessProfile> {
    return this.currentBusinessProfile$;
  }

  closePreferences(): void {
    this.userPreferencesService.closePreferences();
  }

  openReleaseNotes(): void {
    this.dialog.open(ReleaseNotesComponent, {
      width: '1200px',
      data: { appName: this.appName, changeLog: false }
    });
  }

  openChangelog(): void {
    this.dialog.open(ReleaseNotesComponent, {
      width: '1200px',
      data: { appName: this.appName, changeLog: true }
    });
  }

  openMyProfile(): void {
    noop();
  }

  onBusinessProfileReady(): void {
    this.gridEngineFacade?.loadGrids?.();
  }

  onBusinessProfileChange(): void {
    const selectedMasterView = this.gridEngineFacade?.selectedMasterView?.();
    this.favoriteViewsFacade?.resetState?.();
    this.gridEngineFacade?.resetState?.();
    // should keep the same master view when business profile is changed
    this.gridEngineFacade?.selectCurrentMaterView?.(selectedMasterView);
    this.gridScrollStateService?.clearAll?.();
  }

  getNavigationTree(): Observable<SidenavMenuBlock[]> {
    return combineLatest([this.navigationTreeService.getNavigationTree(), this.privileges$]).pipe(
      filter(([_, privileges]) => !!privileges),
      map(([navTree, _]) => {
        const tree = navTree.map((block) => {
          block.items.forEach((item) => {
            item.level = 0;
            item.submenu = [];
          });
          return { ...block, id: HashUtils.simpleHashFromObj(block) };
        });
        return this.authorizationService.getNavigationTree(tree);
      })
    );
  }

  returnHome(): void {
    this.router.navigate(['/', 'home']);
  }
}
